import { useEffect, useState } from "react";
import { api } from "../../services/api";

const priceFields = [
  "pricePerHr", "pricePerMonth", "discountPricePerHr", "discountPricePerMonth",
];

const useAzurePricing = ({ params, enabled, resourceName, onPriceChange, onStatusChange }) => {
  const requestKey = JSON.stringify(params);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    onPriceChange(null);
    onStatusChange?.({ state: enabled ? "loading" : "idle" });
    if (!enabled) return;

    // Debounce changes and discard responses for superseded selections.
    const timeout = setTimeout(async () => {
      try {
        const response = await api.get("/api/AzureMetaData/AzurePricing", {
          params: JSON.parse(requestKey),
        });
        if (!active) return;

        const price = response.data;
        const hasNoPricing = price == null || price === "" ||
          (typeof price === "object" && !Array.isArray(price) && (
            Object.keys(price).length === 0 ||
            (priceFields.every((key) => price[key] === 0) &&
              price.currency == null && price.unitOfMeasure == null)
          ));
        if (hasNoPricing) {
          onStatusChange?.({ state: "unavailable" });
          return;
        }
        if (
          !price ||
          !priceFields.every((key) =>
            typeof price[key] === "number" && Number.isFinite(price[key]) && price[key] >= 0
          ) ||
          typeof price.currency !== "string" ||
          !/^[A-Z]{3}$/.test(price.currency) ||
          typeof price.unitOfMeasure !== "string" ||
          !price.unitOfMeasure.trim()
        ) {
          throw new Error("The Azure pricing API returned an invalid pricing response.");
        }

        setResult({ requestKey, price });
        onPriceChange({ ...price, marketRatePerMonth: price.pricePerMonth });
        onStatusChange?.({ state: "ready" });
      } catch (error) {
        if (!active) return;
        if (error.response?.status === 404) {
          onStatusChange?.({ state: "unavailable" });
          return;
        }
        console.error(`Error fetching Azure ${resourceName} pricing:`, error);
        const message = error.response?.data?.message || error.message ||
          `Failed to fetch Azure ${resourceName} pricing.`;
        onStatusChange?.({ state: "error", message });
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [enabled, requestKey, resourceName, onPriceChange, onStatusChange]);

  return enabled && result?.requestKey === requestKey ? result.price : null;
};

export default useAzurePricing;
